import * as Tone from 'tone';
import Util, { DEFAULT } from './Util';
import SynthInstrument from './SynthInstrument';
import Tile from './Tile';
import { IGrid, ITile } from './Interfaces';

export function makeDefaultInstruments(
  width: number,
  height: number,
  noteLength: number,
): SynthInstrument[] {
  return [
    new SynthInstrument(width, height, noteLength, {
      oscillator: {
        type: 'sine',
      },
      envelope: {
        attack: 0.005,
        decay: 0.1,
        sustain: 0.3,
        release: 1,
      },
    },
    {
      frequency: 1100,
      rolloff: -12,
    }),
    new SynthInstrument(width, height, noteLength, {
      oscillator: {
        type: 'sawtooth',
      },
      envelope: {
        attack: 0.005,
        decay: 0.1,
        sustain: 0.3,
        release: 2,
      },
    },
    {
      frequency: 1100,
      rolloff: -12,
    }),
  ];
}

/** A 2-D matrix that keeps track of notes and can enable, disable, and play them */
export default class Grid implements IGrid {
  public get data(): readonly ITile[] {
    return this._data;
  }

  public get instrumentCount(): number {
    return this.instruments.length;
  }

  private _data: readonly ITile[];
  /**
   * The width of the grid in tiles
   */
  public get width(): number {
    return this._width;
  }

  private _width: number;
  /**
   * The height of the grid in tiles
   */
  public get height(): number {
    return this._height;
  }

  /**
   * Index of the current instrument that's being placed with setTileValue
   */
  public readonly currentInstrument = 0;

  private _height: number;
  private instruments: SynthInstrument[];
  private makeInstruments: (width: number, height: number, noteLength: number) => SynthInstrument[];
  private noteLength: number;
  private defaultNoteLength: boolean;

  /**
   * Creates a new Grid player
   * @param width - The width of the grid in tiles
   * @param height  - The height of the grid in tiles
   * @param renderer - The renderer to use to render the grid
   */
  constructor(
    width: number,
    height: number,
    noteLength?: number,
    makeInstruments = makeDefaultInstruments,
  ) {
    if (noteLength == null) {
      this.defaultNoteLength = true;
      // eslint-disable-next-line no-param-reassign
      noteLength = 1 / width;
    } else {
      this.defaultNoteLength = false;
    }
    this.noteLength = noteLength;
    Tone.Transport.loopEnd = Tone.Time('1m').toSeconds() * (noteLength * width);
    Tone.Transport.loop = true;
    this.noteLength = noteLength;
    this._data = Array.from({ length: width * height }, () => new Tile());
    this._width = width;
    this._height = height;
    this.currentInstrument = 0;
    this.makeInstruments = makeInstruments;
    this.instruments = makeInstruments(width, height, noteLength);
  }

  /**
   * Update the size of the grid.
   * Note: If shrinking, tiles outside the space of
   * new grid will be lost
   *
   * @param newWidth - New width in tiles
   * @param newHeight - New height in tiles
   * @param newNoteLength - New note length. If not set, use previous value,
   * or recalculate the default if using the default value previously.
   * If using the DEFAULT symbol explicitly recalculate the default.
   */
  public setSize(
    newWidth = this.width,
    newHeight = this.height,
    newNoteLength?: number | typeof DEFAULT | null,
  ): void {
    if (newNoteLength === DEFAULT) {
      // eslint-disable-next-line no-param-reassign
      newNoteLength = 1 / this.width;
      this.defaultNoteLength = true;
    } if (newNoteLength == null) {
      // eslint-disable-next-line no-param-reassign
      newNoteLength = this.defaultNoteLength ? 1 / this.width : this.noteLength;
    } else {
      this.defaultNoteLength = false;
    }
    if (
      newWidth === this._width
      && newHeight === this._height
      && newNoteLength === this.noteLength
    ) return;
    this.noteLength = newNoteLength;
    this.instruments.forEach((inst) => inst.dispose());
    this.instruments = this.makeInstruments(newWidth, newHeight, this.noteLength);
    const oldData = this.data;
    this._data = Array.from({ length: newWidth * newHeight }, (_, tileIndex) => {
      const [x, y] = Util.indexToCoord(tileIndex, newHeight as number);
      let tile: ITile;
      if (x >= this._width || y >= this._height) {
        tile = new Tile();
      } else {
        tile = oldData[Util.coordToIndex(x, y, this._height)];
      }
      if (!tile.isEmpty()) {
        this.instruments.forEach((instrument, i) => {
          if (tile.hasNote(i)) {
            tile.removeNote(i);
            tile.addNote(i, instrument.scheduleNote(x, y));
          }
        });
      }
      return tile;
    });
    this._width = newWidth;
    this._height = newHeight;
  }

  getPlayheadX(): number {
    return this.instruments[this.currentInstrument].getPlayheadX();
  }

  /**
   * Gets whether a grid tile is currently lit up (armed)
   * @param x - The x position, measured in grid tiles
   * @param y - The y position, measured in grid tiles
   * @returns Whether the tile is lit up
   */
  getTileValue(x: number, y: number, instrument = this.currentInstrument): boolean {
    return this.data[Util.coordToIndex(x, y, this._height)].hasNote(instrument);
  }

  /**
   * Sets whether a grid tile is currently lit up (armed)
   * @param x - The x position, measured in grid tiles
   * @param y - The y position, measured in grid tiles
   * @param bool - Whether the tile should be turned on (true) or off (false)
   */
  setTileValue(x: number, y: number, bool: boolean, instrument = this.currentInstrument): void {
    if (bool) {
      if (this.getTileValue(x, y, instrument)) return;
      // Turning on, schedule note

      this.data[Util.coordToIndex(x, y, this._height)].addNote(instrument,
        this.instruments[instrument]
          .scheduleNote(x, y));
    } else {
      if (!this.getTileValue(x, y, instrument)) return;
      // Turning off, unschedule note
      this.instruments[instrument]
        .unscheduleNote(this.data[Util.coordToIndex(x, y, this._height)]
          .getNote(instrument));
      this.data[Util.coordToIndex(x, y, this._height)].removeNote(instrument);
    }
  }

  /**
   * Toggles whether a grid tile is currently lit up (armed)
   * @param x - The x position, measured in grid tiles
   * @param y - The y position, measured in grid tiles
   */
  toggleTileValue(x: number, y: number, instrument = this.currentInstrument): void {
    this.setTileValue(x, y, !this.getTileValue(x, y, instrument), instrument);
  }

  /**
   * Turns off all tiles and removes all notes
   */
  clearAllTiles(): void {
    this.data.forEach((e) => e.removeAllNotes());
    this.instruments.forEach((inst) => inst.clearNotes());
    Tone.Transport.cancel();
  }

  /**
   * Set the instrument that the {@link Grid.setTileValue} will put
   * @param instrumentId
   */
  setCurrentInstrument(instrumentId: number): void {
    if (instrumentId >= this.instruments.length) {
      // eslint-disable-next-line no-console
      console.warn('tried to switch to nonexistent instrument');
    } else {
      Object.assign(this, { currentInstrument: instrumentId });
    }
  }

  /**
   * Sets whether the ToneMatrix grid is muted.
   * @param muted - True for muted, false for unmuted
   */
  // eslint-disable-next-line class-methods-use-this
  setMuted(muted: boolean): void {
    Tone.Destination.mute = muted;
  }

  /**
   * Dispose of all resources used by this grid object.
   */
  dispose(): void {
    Tone.Transport.stop();
    Tone.Transport.cancel();
    this.instruments.forEach((inst) => inst.dispose());
    (this.data as Tile[]).length = 0;
    this.instruments.length = 0;
  }
}
